/**
 * PagedComposerView — COMPOSED 보고서의 읽기 전용 A4 낱장 렌더(정본).
 *
 * admin 편집 캔버스(EditorPage `PagedEditBody`)의 낱장 분할과 **같은 rc 공유 엔진**을 쓴다:
 *   컨테이너 DOM 측정(measureBody, data-composer-* 셀렉터) → planEditPages(순수 배치) →
 *   pagePlansEqual 동등 비교로 배치가 바뀔 때만 setState(수렴, 무한루프 방지).
 * 편집 캔버스와 달리 편집 전용 요소(선택 오버레이·클릭 핸들러·MarginCorners·pagedCanvas 재노출)는
 * 없다 — 순수 표시. 같은 표/repeat을 여러 낱장에 다른 rowRange/itemRange로 렌더해도 RenderTemplate이
 * 원본 좌표를 유지하므로 중복이 없다.
 *
 * ⚠️ **문서 페이지번호(`PageNumberLabel`)는 그 「편집 전용」 목록에 들지 않는다** — 출력물의 일부라
 * 이 래퍼와 편집 캔버스가 **같은 조각을 같은 인자로** 렌더한다(결정 2026-09-30). MarginCorners는
 * 캔버스 전용이고 페이지번호는 공유다 — 이 둘을 같은 칸에 두면 다음 사람이 번호를 캔버스 크롬으로
 * 오독한다(그 오독이 실제로 한 번 있었고, 그래서 결정 문서가 쓰였다).
 *
 * 측정 흐름은 **`usePagedPlan` 훅이 소유한다**(stale plan 클립 리셋 · 수렴 가드 · 매 커밋 측정 ·
 * ResizeObserver 구독 · 장수 통지 · EPS). 이 파일에 남은 것은 JSX다 — 종전엔 같은 배선이 이 파일과
 * admin `PagedEditBody`에 **두 벌**이었다(2026-08-19 `/code-review-full` 지적). 그 흐름을 고치려면
 * 여기가 아니라 훅을 보고, 그 헤더가 「무엇을 호스트가 소유하는가」의 정본이다.
 *
 * 위치: 상세/공유/외부결재(web)와 슈퍼관리자 보고서 관리(admin)가 **같은 컴포넌트**를 소비하도록
 * rc 패키지에 둔다(앱별 복제본은 조용히 갈라지므로 금지). web `components/reports/composer/
 * PagedComposerView`는 이 정본을 재노출하는 얇은 wrapper다.
 */
import { RenderTemplate } from './RenderTemplate';
import { buildSliceBody } from './branchPagination';
import type { BranchPaginationContext } from './branchPagination';
import { buildPageChannels } from './pagePlan';
import { usePagedPlan } from './usePagedPlan';
import { pagePx, pageContentHeight } from './page';
import { PageNumberLabel } from './PageNumberLabel';
import type { ReportTemplate } from '@safetyhub/shared';
import type { RenderData, SpecialComponentMap } from './types';

export interface PagedComposerViewProps {
  template: ReportTemplate;
  data: RenderData;
  specialComponents?: SpecialComponentMap;
  /**
   * 각 낱장 div에 부여할 id 접두사. 지정 시 `${prefix}${i}` id를 붙인다(PDF 캡처의 Puppeteer가
   * `#print-page-{i}`로 페이지를 스크린샷). 미지정(상세/공유/결재/admin)이면 id 없음.
   */
  pageIdPrefix?: string;
  /** 낱장 배치 안정화 시 최종 페이지 수를 통지(PDF 캡처의 print-meta data-page-count 동기화). */
  onPageCount?: (n: number) => void;
}

export function PagedComposerView({
  template,
  data,
  specialComponents,
  pageIdPrefix,
  onPageCount,
}: PagedComposerViewProps) {
  const body = template.body;
  const dim = pagePx(template.page);
  const pageContentH = pageContentHeight(template.page);

  // 최상위 branch 투명 통과 입력 — 읽기 전용 렌더라 editing/activeBranchSlots는 없고 조건 평가만
  // 쓴다(렌더 RenderTemplate과 같은 판정). 통짜 plan·측정·slice가 모두 이 하나를 공유해야
  // 계획의 id 집합과 렌더 slice가 갈라지지 않는다.
  // 어떤 훅 deps에도 안 들어가므로 memo는 얻는 게 없다(호스트가 data를 매 렌더 새로 만들면
  // memo도 매번 새 객체다). admin 호스트와 형태를 맞춰 둔다.
  const branchOpts: BranchPaginationContext = { data };

  // 낱장 배치 파이프라인(state 초기화·수렴 가드·클립 리셋·측정 effect·RO 구독·장수 통지)은 rc
  // 공용 훅 하나가 소유한다 — admin `PagedEditBody`가 **같은 훅**을 부른다(사본이면 조용히 갈라지고,
  // 갈린 날 한쪽 호스트에서만 내용이 사라진다. 상세는 usePagedPlan 헤더).
  //
  // ⚠️ 반환값은 **반드시 `pagePlan`이라는 이름으로** 구조분해한다 — 아래 `PageNumberLabel` 호출부가
  // admin 래퍼와 **글자 그대로 같아야** 하고 그것을 admin spec이 소스로 재기 때문이다
  // (`pagedEditBodyPageNumber.test.tsx` `[되돌림] 호출부가 rc 래퍼와 글자 그대로 같다`).
  const { containerRef, pagePlan } = usePagedPlan({ body, pageContentH, branchOpts, onPageCount });

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        // 낱장 가로 정렬은 낱장 자신의 `margin: 0 auto`가 맡는다(아래) — `alignItems:'center'`가
        // 아니다. 중앙 정렬은 같지만 낱장이 컨테이너보다 **넓을 때**가 다르다: alignItems center는
        // 양쪽으로 넘쳐 왼쪽이 스크롤로도 닿지 않고(2026-09-14 가로 낱장 1123px 상세 실측), 음수
        // 여유 공간에서 0으로 접히는 auto margin은 start 정렬이 돼 호스트의 overflow-x-auto로 전부
        // 닿는다(`align-items: safe center`와 같은 동작 — safe 키워드는 브라우저 지원이 갈려 쓰지 않는다).
        gap: 24,
      }}
    >
      {pagePlan.map((items, i) => {
        // 채널 맵 구성은 buildPageChannels(순수) 하나로 — admin PagedEditBody와 같은 함수를 쓴다
        // (키 규칙=인스턴스 경로가 두 호스트에서 갈라지지 않게). body slice는 최상위 항목만.
        const {
          topLevel,
          rowRanges,
          itemRanges,
          leadSuppressed,
          chunkRanges,
          fillHeights,
          fillSlacks,
        } = buildPageChannels(items);
        // 하강한 branch 자식은 buildSliceBody가 그 branch로 다시 감싼다(렌더 wrapper 유지 —
        // 없애면 admin 편집 캔버스의 노드 선택이 깨진다). 하강 안 한 노드는 그대로다.
        const sliceBody = buildSliceBody(topLevel, body, branchOpts);
        return (
          <div
            key={i}
            id={pageIdPrefix ? `${pageIdPrefix}${i}` : undefined}
            // shadow-lg는 web/admin 양쪽 앱 소스에 이미 다수 쓰여 JIT가 생성한다(rc 신규 유틸 아님).
            className="shadow-lg"
            style={{
              width: dim.w,
              minHeight: dim.h,
              background: '#fff',
              position: 'relative',
              flexShrink: 0,
              // safe-center: 여유가 있으면 중앙, 낱장이 컨테이너보다 넓으면 start(위 컨테이너 주석).
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <RenderTemplate
              template={{ ...template, body: sliceBody }}
              data={data}
              specialComponents={specialComponents}
              rowRanges={rowRanges}
              itemRanges={itemRanges}
              leadSuppressed={leadSuppressed}
              chunkRanges={chunkRanges}
              fillHeights={fillHeights}
              fillSlacks={fillSlacks}
            />
            {/* 문서 페이지번호 — admin `PagedEditBody`가 **같은 조각을 같은 인자로** 렌더한다
                (호출부가 갈리면 캔버스가 출력과 다른 번호를 보여 준다). 번호가 없는 낱장에는
                요소가 아예 생기지 않는다. */}
            <PageNumberLabel page={template.page} pageIndex={i} pageCount={pagePlan.length} />
          </div>
        );
      })}
    </div>
  );
}

export default PagedComposerView;
